import { useAppDispatch } from "hooks/hooks";
import { resourcesServices } from "Dashboard/scenes/Resources/services/resource.service";
import { useEffect, useRef, useState } from "react";
import { IoMdPrint } from "react-icons/io";
import PrintRevers from "./PrintRevers";
import classes from "../../../../../../components/Popover/popover.module.scss";
import {
  setReversPopover,
  selectState,
  getReverses,
  selectRevers,
  deleteReversPendingCharge
} from "../main.slice";
import { useSelector } from "react-redux";
import ReversRow from "./ReversRow";
import { toast } from "react-toastify";
import { IRevers } from "Dashboard/scenes/Resources/domain/IRevers";
import { usePagePrint } from "hooks/usePagePrint";
import Modal from "components/Modal/Modal";
import { IReversCharge } from "Dashboard/scenes/Resources/domain/IReversCharge";
import { FormatNumberWithUnitOrNull } from "utils/numberUtils";

interface IProps {
  pendingChargeToDelete: IReversCharge | undefined;
  setPendingChargeToDelete: Function;
}

const ReversPopover = ({
  pendingChargeToDelete,
  setPendingChargeToDelete
}: IProps) => {
  const dispatch = useAppDispatch();
  const { reverses } = useSelector(selectState);
  const currentRevers = useSelector(selectRevers);
  const [noteValue, setNoteValue] = useState<string>("");
  const [selectedChargeIds, setSelectedChargeIds]
    = useState<{ chargeId: number, checked: boolean }[]>([]);
  const [reversForPrint, setReversForPrint]
    = useState<IRevers | undefined>(undefined);

  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getReverses());
  }, [dispatch]);

  useEffect(() => {
    const updatedChargeIdsList = reverses.flatMap(reverse =>
      reverse.charges.map(charge => ({
        chargeId: charge.chargeId,
        checked: true
      }))
    );
    setSelectedChargeIds(updatedChargeIdsList);
  }, [reverses]);

  useEffect(() => {
    if (currentRevers && currentRevers.note !== null) {
      setNoteValue(currentRevers.note);
    } else {
      setNoteValue("");
    }
  }, [currentRevers]);

  const handleChargeSelect = (chargeId: number) => {
    setSelectedChargeIds(prev => {
      return prev.map(c =>
        c.chargeId === chargeId
          ? { ...c, checked: !c.checked }
          : c
      );
    });
  };

  const handlePrint = usePagePrint(componentRef, { margins: "0", landscape: true });

  const handleReversPrint = async () => {
    if (!currentRevers) return;

    try {
      const response: IRevers = !currentRevers.reversId
        ? await resourcesServices.submitPendingRevers({
          chargeIds: selectedChargeIds
            .filter(charge =>
              currentRevers.charges.some(
                chargeOfCurrentRevers =>
                  charge.chargeId === chargeOfCurrentRevers.chargeId &&
                  charge.checked === true
              )
            )
            .map(charge => charge.chargeId),
          note: noteValue
        })
        : await resourcesServices.getSubmittedReversForPrint(
          currentRevers.reversId
        );

      setReversForPrint(response);

      await new Promise(resolve => setTimeout(resolve, 100));

      if (componentRef.current) {
        handlePrint();
        dispatch(setReversPopover({ isOpen: false }));
        setSelectedChargeIds([]);
      }
    } catch (err) {
      console.error('Error in handleReversPrint:', err);
      toast.error("Neočekivana greška pri pripremi za štampanje.");
    }
  };

  const handleDeletePendingCharge = async (chargeId: number) => {
    dispatch(deleteReversPendingCharge(chargeId));
  }

  return (
    <div className={`reversPopover ${classes.basicPopover} loading-container`}>
      {/* {loading && <Loader />} */}
      <Modal
        isOpen={pendingChargeToDelete !== undefined}
        onClick={() => {
          if (pendingChargeToDelete) {
            handleDeletePendingCharge(pendingChargeToDelete.chargeId);
          }
          setPendingChargeToDelete(undefined);
        }}
        onClose={() => setPendingChargeToDelete(undefined)}
        title="Ukloni zaduženje iz reversa"
      >
        <p className="info-msg">
          Da li ste sigurni da želite da uklonite zaduženje '{pendingChargeToDelete?.resourceName} {FormatNumberWithUnitOrNull(pendingChargeToDelete?.quantity, 'kom')}' iz reversa?
          <br />
          <br />
        </p>
      </Modal>
      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir} reversList`}>
          <div className="g-grid g-header">
            <div className={"row reversHeader"}>
              <div className={`col center lowercase separator-line`}>
                red. br.
              </div>
              <div className={`col center lowercase separator-line`}>
                ime
              </div>
              <div className={`col center lowercase separator-line`}>
                gde je resurs
              </div>
            </div>
          </div>
          <div className="g-grid g-content">
            {reverses.map((revers, index) => (
              <ReversRow
                key={index}
                index={index}
                revers={revers}
                handleChargeSelect={handleChargeSelect}
                setPendingChargeToDelete={setPendingChargeToDelete}
              />
            ))}
          </div>

        </div>
      </div>
      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>red. br.</label>
          <input className={`${classes.itemBorder} ${classes.center}`}
            value={currentRevers?.orderNumber ?? ""}
            disabled />
        </div>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>ime</label>
          <input className={classes.itemBorder}
            value={currentRevers?.employeeName ?? ""} disabled />
        </div>
      </div>
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>gde je resurs</label>
          <input className={classes.itemBorder}
            value={currentRevers?.projectName ?? ""} disabled />
        </div>
      </div>
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>napomena</label>
          <textarea rows={3}
            value={noteValue}
            onChange={(e) => setNoteValue(e.target.value)}
          />
        </div>
      </div>

      <div className={classes.row}>
        <div className={classes.buttons}>
          <button className={classes.iconButton}
            onClick={() => { handleReversPrint() }}>
            <IoMdPrint className={classes.icon} />
          </button>
        </div>
      </div>
      <div ref={componentRef} className="print-area">
        <PrintRevers revers={reversForPrint} />
      </div>
    </div>
  );
};

export default ReversPopover;
